import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Database, Search, Filter, ChevronDown, ChevronUp, X, ArrowUpDown } from "lucide-react";
import { useLocation } from "wouter";

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

interface FilterState {
  search: string;
  entityType: string;
  state: string;
  city: string;
  category: string;
  subCategory: string;
  productType: string;
  segment: string;
  region: string;
  neighborhood: string;
  cep: string;
  email: string;
  cpfCnpj: string;
  sourceFile: string;
  deliveryState: string;
  deliveryCity: string;
  createdFrom: string;
  createdTo: string;
  importedFrom: string;
  importedTo: string;
}

const defaultFilters: FilterState = {
  search: "",
  entityType: "all",
  state: "all",
  city: "",
  category: "all",
  subCategory: "all",
  productType: "all",
  segment: "all",
  region: "all",
  neighborhood: "",
  cep: "",
  email: "",
  cpfCnpj: "",
  sourceFile: "all",
  deliveryState: "",
  deliveryCity: "",
  createdFrom: "",
  createdTo: "",
  importedFrom: "",
  importedTo: "",
};

export default function EntitiesPage() {
  const [, navigate] = useLocation();
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const limit = 20;

  const debouncedFilters = useDebounce(filters, 400);

  const updateFilter = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    setPage(0);
  };

  const activeFilterCount = Object.entries(filters).filter(([key, val]) => {
    if (key === "search") return val !== "";
    if (["city", "neighborhood", "cep", "email", "cpfCnpj", "deliveryState", "deliveryCity", "createdFrom", "createdTo", "importedFrom", "importedTo"].includes(key)) return val !== "";
    return val !== "all";
  }).length;

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setPage(0);
  };

  const { data: filterOptions } = useQuery({
    queryKey: ["/api/entities/filter-options"],
    queryFn: async () => {
      const response = await fetch("/api/entities/filter-options");
      if (!response.ok) return null;
      return response.json();
    },
  });

  const { data: cities } = useQuery({
    queryKey: ["/api/entities/cities", filters.state],
    queryFn: async () => {
      if (!filters.state || filters.state === "all") return [];
      const response = await fetch(`/api/entities/cities/${filters.state}`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: filters.state !== "all" && filters.state !== "",
  });

  const { data: entities, isLoading } = useQuery({
    queryKey: ["/api/entities", debouncedFilters, page, sortField, sortOrder],
    queryFn: async () => {
      const f = debouncedFilters;
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
        sort: sortField,
        order: sortOrder,
      });
      if (f.search) params.append("search", f.search);
      if (f.entityType !== "all") params.append("entityType", f.entityType);
      if (f.state !== "all") params.append("state", f.state);
      if (f.city) params.append("city", f.city);
      if (f.category !== "all") params.append("category", f.category);
      if (f.subCategory !== "all") params.append("subCategory", f.subCategory);
      if (f.productType !== "all") params.append("productType", f.productType);
      if (f.segment !== "all") params.append("segment", f.segment);
      if (f.region !== "all") params.append("region", f.region);
      if (f.neighborhood) params.append("neighborhood", f.neighborhood);
      if (f.cep) params.append("cep", f.cep);
      if (f.email) params.append("email", f.email);
      if (f.cpfCnpj) params.append("cpfCnpj", f.cpfCnpj);
      if (f.sourceFile !== "all") params.append("sourceFile", f.sourceFile);
      if (f.deliveryState) params.append("deliveryState", f.deliveryState);
      if (f.deliveryCity) params.append("deliveryCity", f.deliveryCity);
      if (f.createdFrom) params.append("createdFrom", f.createdFrom);
      if (f.createdTo) params.append("createdTo", f.createdTo);
      if (f.importedFrom) params.append("importedFrom", f.importedFrom);
      if (f.importedTo) params.append("importedTo", f.importedTo);

      const response = await fetch(`/api/entities?${params}`);
      if (!response.ok) throw new Error("Erro ao buscar entidades");
      return response.json();
    },
  });

  const totalPages = entities ? Math.ceil(entities.total / limit) : 0;

  const SortableHeader = ({ field, children }: { field: string; children: string }) => (
    <TableHead
      className="cursor-pointer select-none hover:bg-muted/50 transition-colors"
      onClick={() => handleSort(field)}
      data-testid={`sort-${field}`}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown className={`w-3 h-3 ${sortField === field ? "text-primary" : "text-muted-foreground/50"}`} />
        {sortField === field && (
          <span className="text-xs text-primary">{sortOrder === "asc" ? "↑" : "↓"}</span>
        )}
      </div>
    </TableHead>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
          <Database className="w-5 h-5 text-purple-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold" data-testid="text-page-title">Consultar Dados</h2>
          <p className="text-muted-foreground">Visualize e pesquise entidades cadastradas</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros
            {activeFilterCount > 0 && (
              <Badge variant="secondary" data-testid="text-active-filters">{activeFilterCount} ativo(s)</Badge>
            )}
          </CardTitle>
          <CardDescription>Refine sua busca com os filtros abaixo</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Nome, CPF/CNPJ, Codigo, Tel..."
                  value={filters.search}
                  onChange={(e) => updateFilter("search", e.target.value)}
                  className="pl-10"
                  data-testid="input-search"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Tipo</label>
              <Select value={filters.entityType} onValueChange={(v) => updateFilter("entityType", v)}>
                <SelectTrigger data-testid="select-entity-type">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {filterOptions?.types?.map((t: string) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Estado</label>
              <Select value={filters.state} onValueChange={(v) => { updateFilter("state", v); updateFilter("city", ""); }}>
                <SelectTrigger data-testid="select-state">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {filterOptions?.states?.map((s: string) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Categoria</label>
              <Select value={filters.category} onValueChange={(v) => updateFilter("category", v)}>
                <SelectTrigger data-testid="select-category">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {filterOptions?.categories?.map((c: string) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-muted-foreground"
            data-testid="button-toggle-advanced"
          >
            {showAdvanced ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
            Filtros Avancados
          </Button>

          {showAdvanced && (
            <div className="space-y-4 border-t pt-4">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {filters.state !== "all" && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Cidade</label>
                    <Select value={filters.city || "all"} onValueChange={(v) => updateFilter("city", v === "all" ? "" : v)}>
                      <SelectTrigger data-testid="select-city">
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {Array.isArray(cities) && cities.map((c: string) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium">Subcategoria</label>
                  <Select value={filters.subCategory} onValueChange={(v) => updateFilter("subCategory", v)}>
                    <SelectTrigger data-testid="select-sub-category">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {filterOptions?.subCategories?.map((s: string) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Tipo de Produto</label>
                  <Select value={filters.productType} onValueChange={(v) => updateFilter("productType", v)}>
                    <SelectTrigger data-testid="select-product-type">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {filterOptions?.productTypes?.map((p: string) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Segmento</label>
                  <Select value={filters.segment} onValueChange={(v) => updateFilter("segment", v)}>
                    <SelectTrigger data-testid="select-segment">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {filterOptions?.segments?.map((s: string) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Regiao</label>
                  <Select value={filters.region} onValueChange={(v) => updateFilter("region", v)}>
                    <SelectTrigger data-testid="select-region">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {filterOptions?.regions?.map((r: string) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Arquivo Fonte</label>
                  <Select value={filters.sourceFile} onValueChange={(v) => updateFilter("sourceFile", v)}>
                    <SelectTrigger data-testid="select-source-file">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {filterOptions?.sourceFiles?.map((f: string) => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">CPF/CNPJ</label>
                  <Input
                    placeholder="Buscar CPF ou CNPJ..."
                    value={filters.cpfCnpj}
                    onChange={(e) => updateFilter("cpfCnpj", e.target.value)}
                    data-testid="input-cpf-cnpj"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">E-mail</label>
                  <Input
                    placeholder="Buscar por e-mail..."
                    value={filters.email}
                    onChange={(e) => updateFilter("email", e.target.value)}
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Bairro</label>
                  <Input
                    placeholder="Buscar bairro..."
                    value={filters.neighborhood}
                    onChange={(e) => updateFilter("neighborhood", e.target.value)}
                    data-testid="input-neighborhood"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">CEP</label>
                  <Input
                    placeholder="Buscar CEP..."
                    value={filters.cep}
                    onChange={(e) => updateFilter("cep", e.target.value)}
                    data-testid="input-cep"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Estado Entrega</label>
                  <Input
                    placeholder="UF de entrega..."
                    value={filters.deliveryState}
                    onChange={(e) => updateFilter("deliveryState", e.target.value.toUpperCase())}
                    maxLength={2}
                    data-testid="input-delivery-state"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Cidade Entrega</label>
                  <Input
                    placeholder="Cidade de entrega..."
                    value={filters.deliveryCity}
                    onChange={(e) => updateFilter("deliveryCity", e.target.value)}
                    data-testid="input-delivery-city"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Criado De</label>
                  <Input
                    type="date"
                    value={filters.createdFrom}
                    onChange={(e) => updateFilter("createdFrom", e.target.value)}
                    data-testid="input-created-from"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Criado Ate</label>
                  <Input
                    type="date"
                    value={filters.createdTo}
                    onChange={(e) => updateFilter("createdTo", e.target.value)}
                    data-testid="input-created-to"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Importado De</label>
                  <Input
                    type="date"
                    value={filters.importedFrom}
                    onChange={(e) => updateFilter("importedFrom", e.target.value)}
                    data-testid="input-imported-from"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Importado Ate</label>
                  <Input
                    type="date"
                    value={filters.importedTo}
                    onChange={(e) => updateFilter("importedTo", e.target.value)}
                    data-testid="input-imported-to"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={clearFilters}
              data-testid="button-clear-filters"
            >
              <X className="w-4 h-4 mr-1" />
              Limpar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Resultados
            {entities && (
              <span className="text-sm font-normal text-muted-foreground ml-2" data-testid="text-total-count">
                ({entities.total.toLocaleString("pt-BR")} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Carregando...</div>
          ) : !entities?.data || entities.data.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="text-no-results">
              Nenhuma entidade encontrada
            </div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader field="name">Nome</SortableHeader>
                      <SortableHeader field="cpfCnpj">CPF/CNPJ</SortableHeader>
                      <SortableHeader field="city">Cidade</SortableHeader>
                      <SortableHeader field="state">UF</SortableHeader>
                      <TableHead>Telefone</TableHead>
                      <TableHead>Celular</TableHead>
                      <SortableHeader field="category">Categoria</SortableHeader>
                      <SortableHeader field="segment">Segmento</SortableHeader>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entities.data.map((entity: any) => (
                      <TableRow
                        key={entity.id}
                        data-testid={`row-entity-${entity.id}`}
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => navigate(`/entities/${entity.id}`)}
                      >
                        <TableCell className="font-medium">
                          {entity.name || "-"}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {entity.cpfCnpj || "-"}
                        </TableCell>
                        <TableCell>{entity.city || "-"}</TableCell>
                        <TableCell>{entity.state || "-"}</TableCell>
                        <TableCell className="text-sm">{entity.phone || "-"}</TableCell>
                        <TableCell className="text-sm">{entity.cellphone || "-"}</TableCell>
                        <TableCell>
                          {entity.category ? <Badge variant="outline">{entity.category}</Badge> : "-"}
                        </TableCell>
                        <TableCell className="text-sm">{entity.segment || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Pagina {page + 1} de {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 0}
                      data-testid="button-prev-page"
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages - 1}
                      data-testid="button-next-page"
                    >
                      Proxima
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
